/**
 * Chart Multi-Pane Layout and Grid Geometry Manager.
 *
 * Coordinates multi-pane layout switching (single, 2 horizontal, 2 vertical, 2+1, 4 grid),
 * CSS grid placement math, and saved pane state resolution.
 */

export type LayoutType = "single" | "2h" | "2v" | "2plus1" | "4grid";

export interface PanePlacement {
  gridColumn: string;
  gridRow: string;
}

export interface PaneConfig {
  symbol: string;
  timeframe: string;
}

export const DEFAULT_LAYOUT_SYMBOLS = [
  "NSE_INDEX|Nifty 50",
  "BSE_INDEX|SENSEX",
  "NSE_INDEX|Nifty Bank",
  "NSE_INDEX|Nifty 50",
];

/**
 * Returns the number of visible chart panes for the selected layout.
 */
export function getPaneCount(layout: LayoutType | string): number {
  switch (layout) {
    case "single":
      return 1;
    case "2h":
    case "2v":
      return 2;
    case "2plus1":
      return 3;
    case "4grid":
      return 4;
    default:
      return 1;
  }
}

/**
 * Calculates CSS grid column/row placements for a pane in a given layout.
 *
 * 2+1 layout ("two stacked on left, one full-height on right"):
 * - Pane 0: Column 1, Row 1
 * - Pane 1: Column 1, Row 2
 * - Pane 2: Column 2, Row 1 / 3 (spans both rows)
 */
export function getPaneGridPlacement(
  layout: LayoutType | string,
  index: number,
): PanePlacement {
  if (layout === "2plus1") {
    if (index === 2) {
      return {
        gridColumn: "2",
        gridRow: "1 / 3",
      };
    }
    return {
      gridColumn: "1",
      gridRow: String(index + 1),
    };
  }

  return {
    gridColumn: "",
    gridRow: "",
  };
}

/**
 * Resolves pane configs by merging persisted layout state with default symbol rotation.
 */
export function resolvePaneConfigs(
  savedPanes: PaneConfig[] | undefined,
  targetCount: number,
  defaultSymbols: string[] = DEFAULT_LAYOUT_SYMBOLS,
): PaneConfig[] {
  const result: PaneConfig[] = [];
  const symCount = defaultSymbols.length || 1;

  for (let i = 0; i < targetCount; i++) {
    if (savedPanes && savedPanes[i] && savedPanes[i].symbol) {
      result.push({
        symbol: savedPanes[i].symbol,
        timeframe: savedPanes[i].timeframe || "3m",
      });
    } else {
      result.push({
        symbol: defaultSymbols[i % symCount],
        timeframe: "3m",
      });
    }
  }

  return result;
}
